﻿import { useMemo, useState } from "react";
import { AdminButton, AdminPagination, AdminSearchInput, AdminStatusBadge, AdminTable, AdminTd, AdminTr } from "@/components/admin";
import { formatDateTime } from "@/lib/format";
import type { DeviceItem } from "@/types.ts";
interface DeviceListProps {
    devices: DeviceItem[];
    selectedDeviceId: number | null;
    searchQuery: string;
    activeFilter: string;
    totalCount: number;
    loading: boolean;
    onSearchChange: (val: string) => void;
    onSelect: (device: DeviceItem) => void;
    onRefreshClick: () => void;
}
const statusLabel = (device: DeviceItem) => {
    return device.statusShortName ?? device.statusName ?? device.status;
};
const matchesFilter = (device: DeviceItem, filter: string) => {
    if (filter === "all")
        return true;
    return device.status === filter;
};
const DeviceList = ({ devices, selectedDeviceId, searchQuery, activeFilter, totalCount, loading, onSearchChange, onSelect, onRefreshClick, }: DeviceListProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const filtered = useMemo(() => {
        let result = devices.filter((device) => matchesFilter(device, activeFilter));
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            result = result.filter((device) => [
                device.deviceId,
                device.seniorUserAccountId,
                device.elderlyName,
                device.deviceName,
                device.deviceUuid,
                device.platformType,
                device.status,
                device.statusName,
                device.statusShortName,
            ]
                .filter((value) => value != null)
                .some((value) => String(value).toLowerCase().includes(q)));
        }
        return result;
    }, [devices, activeFilter, searchQuery]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * pageSize;
    const endIdx = Math.min(startIdx + pageSize, filtered.length);
    const pageItems = filtered.slice(startIdx, endIdx);
    return (<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <i className="ri-smartphone-line text-blue-500 text-sm"/>
          <h3 className="text-sm font-semibold text-slate-800">기기 목록</h3>
          <span className="text-xs text-slate-400">총 {totalCount}대</span>
          {activeFilter !== "all" && <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">필터 적용 중</span>}
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <AdminSearchInput value={searchQuery} onChange={(value) => {
            onSearchChange(value);
            setCurrentPage(1);
          }} placeholder="기기명, UUID, 어르신명, ID 검색" className="min-w-0 flex-1 sm:w-64 sm:flex-none"/>
          <AdminButton variant="soft" size="sm" icon={`ri-refresh-line ${loading ? "animate-spin" : ""}`} onClick={onRefreshClick} disabled={loading} className="shrink-0">
            새로고침
          </AdminButton>
        </div>
      </div>

      <AdminTable
        headers={["기기", "연결 어르신", "상태", "플랫폼", "앱 버전", "최근 온라인"]}
        loading={loading}
        emptyMessage={pageItems.length === 0 ? "표시할 기기 데이터가 없습니다." : undefined}
      >
        {pageItems.map((device) => {
            const isSelected = selectedDeviceId === device.deviceId;
            return (<AdminTr key={device.deviceId} className={`cursor-pointer ${isSelected ? "bg-blue-50/60" : "hover:bg-slate-50"}`} onClick={() => onSelect(device)}>
                    <AdminTd>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-800">{device.deviceName ?? `기기 #${device.deviceId}`}</span>
                        <span className="text-xs text-slate-400 mt-0.5">{device.deviceUuid}</span>
                      </div>
                    </AdminTd>
                    <AdminTd>
                      <div className="flex flex-col">
                        <span className="text-sm text-slate-700">{device.elderlyName}</span>
                        <span className="text-xs text-slate-400 mt-0.5">ID {device.seniorUserAccountId}</span>
                      </div>
                    </AdminTd>
                    <AdminTd><AdminStatusBadge group="DEVICE_STATUS" code={device.status} label={statusLabel(device)}/></AdminTd>
                    <AdminTd>
                      <span className="text-xs text-slate-600">{device.platformType}</span>
                    </AdminTd>
                    <AdminTd>
                      <span className="text-xs text-slate-600">{device.appVersion ?? "-"}</span>
                    </AdminTd>
                    <AdminTd>
                      <span className="text-xs text-slate-500">{formatDateTime(device.lastOnlineAt)}</span>
                    </AdminTd>
                  </AdminTr>);
        })}
      </AdminTable>

      <AdminPagination currentPage={safePage} totalPages={totalPages} totalCount={filtered.length} pageSize={pageSize} itemLabel="대" onPageChange={setCurrentPage}/>
    </div>);
};
export default DeviceList;
